import { Link, useNavigate } from "react-router-dom";
import { User, logout } from "../services/api";
import { useBranding } from "../hooks/useBranding";
import { hasAnyRole } from "../utils/permissions";
import styles from "./NavBar.module.css";

export const NavBar: React.FC<{
  user: User | null;
  roles?: string[];
  onLogout?: () => void;
}> = ({ user, roles = [], onLogout }) => {
  const { appName, logoUrl } = useBranding();
  const navigate = useNavigate();

  const handleLogout = async () => {
    const success = await logout();
    if (success && onLogout) {
      onLogout();
    }
    navigate("/login");
  };

  // Abgemeldet: Logo zentriert, keine Buttons, kein Profil
  if (user === null) {
    return (
      <nav className={`navbar ${styles.navbar} ${styles.navbarLogout} d-flex justify-content-center align-items-center`}>
        <a href="/" className="mx-auto">
          <img
            src={logoUrl}
            alt={appName}
            style={{ height: 50, verticalAlign: "middle" }}
          />
        </a>
      </nav>
    );
  }

  // Handler zum Schließen des Menüs
  const closeMenu = () => {
    try {
      const navbarCollapse = document.getElementById('navbarContent');
      if (navbarCollapse && navbarCollapse.classList.contains('show')) {
        // Einfach die 'show' Klasse entfernen - funktioniert zuverlässiger
        navbarCollapse.classList.remove('show');
        
        // Toggle-Button Status zurücksetzen
        const toggleButton = document.querySelector('[data-bs-target="#navbarContent"]');
        if (toggleButton) {
          toggleButton.setAttribute('aria-expanded', 'false');
          toggleButton.classList.add('collapsed');
        }
      }
    } catch (error) {
      console.error('Error closing menu:', error);
    }
  };

  // Angemeldet: Responsive Layout
  return (
    <nav className={`navbar navbar-expand-lg navbar-light bg-light fixed-top ${styles.navbar} ${styles.navbarLight} ${styles.bgLight} ${styles.fixedTop}`}>
      <div className={`container-fluid ${styles.containerFluid}`}>
        
        {/* Mobile Layout */}
        <div className="d-lg-none w-100">
          <div className="d-flex justify-content-between align-items-center">
            {/* Profilbild links auf Mobile */}
            <Link to="/profile" className="d-flex align-items-center" onClick={closeMenu}>
              {user.picture ? (
                <img src={`data:image/png;base64,${user.picture}`} className={`rounded-circle ${styles.userProfileImage}`} width="35px" height="35px" alt="User Image" />
              ) : (
                <img src="/static/images/user-profile-icon.png" className={`rounded-circle ${styles.userProfileImage}`} width="35px" height="35px" alt="User Image" />
              )}
            </Link>
            
            {/* Logo zentriert */}
            <Link className={`navbar-brand ${styles.navbarBrand} position-absolute start-50 translate-middle-x`} to="/" onClick={closeMenu}>
              <img src={logoUrl} alt="Logo" style={{ height: 40 }} />
            </Link>
            
            {/* Burger-Button rechts - SICHTBAR */}
            <button
              className="navbar-toggler border-0"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarContent"
              aria-controls="navbarContent"
              aria-expanded="false"
              aria-label="Toggle navigation"
              style={{ padding: '0.25rem 0.5rem' }}
            >
              <span className="navbar-toggler-icon"></span>
            </button>
          </div>
          
          {/* Mobile Menu */}
          <div className={`collapse navbar-collapse mt-3 ${styles.navbarCollapse}`} id="navbarContent">
            <div className="w-100">
              {/* User Info Card */}
              <div className={`card mb-3 ${styles.mobileUserCard}`}>
                <div className="card-body text-center py-3">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    {user.picture ? (
                      <img src={`data:image/png;base64,${user.picture}`} className={`rounded-circle me-3 ${styles.userProfileImage}`} width="50px" height="50px" alt="User Image" />
                    ) : (
                      <img src="/static/images/user-profile-icon.png" className={`rounded-circle me-3 ${styles.userProfileImage}`} width="50px" height="50px" alt="User Image" />
                    )}
                    <div className="text-start">
                      <div className="fw-bold">{user.firstname} {user.lastname}</div>
                      <small className="text-muted">{user.mail}</small>
                    </div>
                  </div>
                  <div className="d-grid gap-2 d-md-flex justify-content-md-center">
                    <Link className="btn btn-outline-primary btn-sm" to="/profile" onClick={closeMenu}>
                      Profile
                    </Link>
                    <button className="btn btn-outline-danger btn-sm" onClick={() => { closeMenu(); handleLogout(); }}>
                      Logout
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Navigation Links */}
              <div className={`list-group ${styles.mobileNavList}`}>
                <Link className={`list-group-item list-group-item-action d-flex align-items-center ${styles.mobileNavItem}`} to="/events" onClick={closeMenu}>
                  <i className="bi bi-calendar-event me-3"></i>
                  Events
                </Link>
                
                {(hasAnyRole(roles, ["Lunch-Roulette Admin"]) || (user.mail.endsWith("@ypog.law"))) && (
                  <Link className={`list-group-item list-group-item-action d-flex align-items-center ${styles.mobileNavItem}`} to="/lunch-roulette" onClick={closeMenu}>
                    <i className="bi bi-people me-3"></i>
                    Lunch Roulettes
                  </Link>
                )}
                
                {/* Admin Section */}
                {hasAnyRole(roles, ["Admin", "User Admin", "User Role Admin", "Firm Admin", "Location Admin", "Error Admin", "Event Admin", "Event Report", "Job Admin", "Log Admin", "Role Admin"]) && (
                  <div className="mt-3">
                    <h6 className="text-muted px-3 mb-2 small text-uppercase fw-bold">Administration</h6>
                    <div className={`list-group ${styles.mobileAdminList}`}>
                      <Link className={`list-group-item list-group-item-action d-flex align-items-center ${styles.mobileNavItem}`} to="/admin" onClick={closeMenu}>
                        <i className="bi bi-speedometer2 me-3"></i>
                        Admin Dashboard
                      </Link>
                      
                      {hasAnyRole(roles, ["Admin", "User Admin", "User Role Admin"]) && (
                        <Link className={`list-group-item list-group-item-action d-flex align-items-center ${styles.mobileNavItem}`} to="/admin/users" onClick={closeMenu}>
                          <i className="bi bi-person-gear me-3"></i>
                          User Management
                        </Link>
                      )}
                      
                      {hasAnyRole(roles, ["Admin", "Firm Admin"]) && (
                        <Link className={`list-group-item list-group-item-action d-flex align-items-center ${styles.mobileNavItem}`} to="/admin/firms" onClick={closeMenu}>
                          <i className="bi bi-building me-3"></i>
                          Firm Management
                        </Link>
                      )}
                      
                      {hasAnyRole(roles, ["Admin", "Location Admin"]) && (
                        <Link className={`list-group-item list-group-item-action d-flex align-items-center ${styles.mobileNavItem}`} to="/admin/locations" onClick={closeMenu}>
                          <i className="bi bi-geo-alt me-3"></i>
                          Location Management
                        </Link>
                      )}
                      
                      {hasAnyRole(roles, ["Admin", "Error Admin"]) && (
                        <Link className={`list-group-item list-group-item-action d-flex align-items-center ${styles.mobileNavItem}`} to="/admin/errors" onClick={closeMenu}>
                          <i className="bi bi-exclamation-triangle me-3"></i>
                          Error Management
                        </Link>
                      )}
                      
                      
                      {hasAnyRole(roles, ["Admin", "Job Admin", "Log Admin"]) && (
                        <Link className={`list-group-item list-group-item-action d-flex align-items-center ${styles.mobileNavItem}`} to="/admin/jobs" onClick={closeMenu}>
                          <i className="bi bi-gear me-3"></i>
                          Job Management
                        </Link>
                      )}
                      
                      {hasAnyRole(roles, ["Admin", "Role Admin", "User Role Admin"]) && (
                        <Link className={`list-group-item list-group-item-action d-flex align-items-center ${styles.mobileNavItem}`} to="/admin/roles" onClick={closeMenu}>
                          <i className="bi bi-shield-check me-3"></i>
                          Role Management
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* App Version */}
              <div className="text-center mt-4 mb-3">
                <small className="text-muted">App Version {process.env.REACT_APP_VERSION}</small>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="d-none d-lg-flex w-100 align-items-center position-relative">
          {/* Logo links ohne App-Name */}
          <Link className={`navbar-brand ${styles.navbarBrand} d-flex align-items-center`} to="/">
            <img src={logoUrl} alt="Logo" style={{ height: 45 }} />
          </Link>

          {/* Moderne zentrierte Navigation - absolut zentriert */}
          <div className={`${styles.desktopNavContainer} position-absolute start-50 translate-middle-x`}>
            <ul className={`navbar-nav ${styles.navbarNav} d-flex flex-row gap-1`}>
              <li className={`nav-item ${styles.navItem}`}>
                <Link className={`nav-link ${styles.navLink} ${styles.modernNavLink} px-4 py-2 rounded-pill`} to="/events">
                  <i className="bi bi-calendar-event me-2"></i>
                  Events
                </Link>
              </li>
              {(hasAnyRole(roles, ["Lunch-Roulette Admin"]) || (user.mail.endsWith("@ypog.law"))) && (
                <li className={`nav-item ${styles.navItem}`}>
                  <Link className={`nav-link ${styles.navLink} ${styles.modernNavLink} px-4 py-2 rounded-pill`} to="/lunch-roulette">
                    <i className="bi bi-people me-2"></i>
                    Lunch Roulettes
                  </Link>
                </li>
              )}
              {hasAnyRole(roles, ["Admin", "User Admin", "User Role Admin", "Firm Admin", "Location Admin", "Error Admin", "Event Admin", "Event Report", "Job Admin", "Log Admin", "Role Admin"]) && (
                <li className={`nav-item dropdown ${styles.navItem}`}>
                  <a
                    className={`nav-link dropdown-toggle ${styles.navLink} ${styles.modernNavLink} ${styles.dropdownToggle} px-4 py-2 rounded-pill`}
                    href="#"
                    id="navbarScrollingDropdown1"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="bi bi-gear me-2"></i>
                    Admin
                  </a>
                  <ul className={`dropdown-menu ${styles.dropdownMenu} ${styles.modernDropdown} shadow-lg border-0 rounded-3 mt-2`}>
        
                      <li>
                        <Link className={`dropdown-item ${styles.dropdownItem} ${styles.modernDropdownItem} d-flex align-items-center`} to="/admin">
                          <i className="bi bi-speedometer2 me-3 text-primary"></i>
                          <div>
                            <div className="fw-semibold">Admin Dashboard</div>
                            <small className="text-muted">Main Administration</small>
                          </div>
                        </Link>
                      </li>
           
                    <li><hr className="dropdown-divider my-2" /></li>
                    {hasAnyRole(roles, ["Admin", "User Admin", "User Role Admin"]) && (
                      <li>
                        <Link className={`dropdown-item ${styles.dropdownItem} ${styles.modernDropdownItem} d-flex align-items-center`} to="/admin/users">
                          <i className="bi bi-person-gear me-3 text-success"></i>
                          <div>
                            <div className="fw-semibold">User Management</div>
                            <small className="text-muted">User Administration</small>
                          </div>
                        </Link>
                      </li>
                    )}
                    {hasAnyRole(roles, ["Admin", "Firm Admin"]) && (
                      <li>
                        <Link className={`dropdown-item ${styles.dropdownItem} ${styles.modernDropdownItem} d-flex align-items-center`} to="/admin/firms">
                          <i className="bi bi-building me-3 text-info"></i>
                          <div>
                            <div className="fw-semibold">Firm Management</div>
                            <small className="text-muted">Law Firm Administration</small>
                          </div>
                        </Link>
                      </li>
                    )}
                    {hasAnyRole(roles, ["Admin", "Location Admin"]) && (
                      <li>
                        <Link className={`dropdown-item ${styles.dropdownItem} ${styles.modernDropdownItem} d-flex align-items-center`} to="/admin/locations">
                          <i className="bi bi-geo-alt me-3 text-warning"></i>
                          <div>
                            <div className="fw-semibold">Location Management</div>
                            <small className="text-muted">Office Location Administration</small>
                          </div>
                        </Link>
                      </li>
                    )}
                    {hasAnyRole(roles, ["Admin", "Error Admin"]) && (
                      <li>
                        <Link className={`dropdown-item ${styles.dropdownItem} ${styles.modernDropdownItem} d-flex align-items-center`} to="/admin/errors">
                          <i className="bi bi-exclamation-triangle me-3 text-danger"></i>
                          <div>
                            <div className="fw-semibold">Error Management</div>
                            <small className="text-muted">Error Administration</small>
                          </div>
                        </Link>
                      </li>
                    )}

                    {hasAnyRole(roles, ["Admin", "Job Admin", "Log Admin"]) && (
                      <li>
                        <Link className={`dropdown-item ${styles.dropdownItem} ${styles.modernDropdownItem} d-flex align-items-center`} to="/admin/jobs">
                          <i className="bi bi-gear me-3 text-secondary"></i>
                          <div>
                            <div className="fw-semibold">Job Management</div>
                            <small className="text-muted">Job Administration</small>
                          </div>
                        </Link>
                      </li>
                    )}
                    {hasAnyRole(roles, ["Admin", "Role Admin", "User Role Admin"]) && (
                      <li>
                        <Link className={`dropdown-item ${styles.dropdownItem} ${styles.modernDropdownItem} d-flex align-items-center`} to="/admin/roles">
                          <i className="bi bi-shield-check me-3 text-success"></i>
                          <div>
                            <div className="fw-semibold">Role Management</div>
                            <small className="text-muted">Role Administration</small>
                          </div>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              )}
            </ul>
          </div>

          {/* User Profile Area - absolut rechts positioniert */}
          <div className={`d-flex align-items-center position-absolute end-0 ${styles.userContainer}`}>
            {/* User Profile Dropdown */}
            <div className="dropdown">
              <button
                className={`btn d-flex align-items-center ${styles.userProfileBtn} border-0 bg-transparent`}
                type="button"
                id="userProfileDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <div className="text-end me-3 d-none d-xl-block">
                  <div className="fw-semibold text-dark" style={{ fontSize: '0.9rem' }}>{user.firstname} {user.lastname}</div>
                  <small className="text-muted">{user.mail}</small>
                </div>
                <div className="position-relative">
                  {user.picture ? (
                    <img src={`data:image/png;base64,${user.picture}`} className={`rounded-circle ${styles.userProfileImage} ${styles.modernProfileImage}`} width="45px" height="45px" alt="User Image" />
                  ) : (
                    <img src="/static/images/user-profile-icon.png" className={`rounded-circle ${styles.userProfileImage} ${styles.modernProfileImage}`} width="45px" height="45px" alt="User Image" />
                  )}
                  <span className={`position-absolute bottom-0 end-0 bg-success rounded-circle ${styles.onlineIndicator}`}></span>
                </div>
              </button>
              <ul className={`dropdown-menu dropdown-menu-end ${styles.dropdownMenu} ${styles.modernUserDropdown} shadow-lg border-0 rounded-3 mt-2`}>
                <li className="px-3 py-2">
                  <div className="d-flex align-items-center">
                    {user.picture ? (
                      <img src={`data:image/png;base64,${user.picture}`} className="rounded-circle me-3" width="40px" height="40px" alt="User Image" />
                    ) : (
                      <img src="/static/images/user-profile-icon.png" className="rounded-circle me-3" width="40px" height="40px" alt="User Image" />
                    )}
                    <div>
                      <div className="fw-bold">{user.firstname} {user.lastname}</div>
                      <small className="text-muted">{user.mail}</small>
                    </div>
                  </div>
                </li>
                <li><hr className="dropdown-divider my-2" /></li>
                <li>
                  <Link className={`dropdown-item ${styles.dropdownItem} ${styles.modernDropdownItem} d-flex align-items-center`} to="/profile">
                    <i className="bi bi-person me-3 text-primary"></i>
                    <div>
                      <div className="fw-semibold">My Profile</div>
                      <small className="text-muted">Edit profile</small>
                    </div>
                  </Link>
                </li>
                <li><hr className="dropdown-divider my-2" /></li>
                <li>
                  <button className={`dropdown-item ${styles.dropdownItem} ${styles.modernDropdownItem} d-flex align-items-center`} onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right me-3 text-danger"></i>
                    <div>
                      <div className="fw-semibold">Sign Out</div>
                      <small className="text-muted">End session</small>
                    </div>
                  </button>
                </li>
                <li><hr className="dropdown-divider my-1" /></li>
                <li className="px-3 py-2">
                  <small className="text-muted d-flex align-items-center">
                    <i className="bi bi-info-circle me-2"></i>
                    App Version {process.env.REACT_APP_VERSION}
                  </small>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
